import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ChopLeistungsgruppe } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { FormButtonBarComponent } from '@shared/form-button-bar/form-button-bar.component';
import { FormNumberInputComponent } from '@shared/form-number-input/form-number-input.component';
import { FormTextInputComponent } from '@shared/form-text-input/form-text-input.component';
import { applyChopServerValidationError } from '../../../chop-server-errors';

const FIELD_MAP = { Nummer: 'nummer', 'Bezeichnung.De': 'bezeichnung.De' } as const;

@Component({
  selector: 'app-chop-leistungsgruppe-dialog',
  templateUrl: './chop-leistungsgruppe-dialog.component.html',
  styleUrls: ['./chop-leistungsgruppe-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, FormTextInputComponent, FormNumberInputComponent, FormButtonBarComponent],
})
export class ChopLeistungsgruppeDialogComponent {
  private formBuilder = inject(FormBuilder);
  private dialogConfig = inject(DynamicDialogConfig);
  private dialogRef = inject(DynamicDialogRef);
  private chopService = inject(ChopService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  private alterskategorieId: number = this.dialogConfig.data.alterskategorieId;
  private leistungsgruppe: ChopLeistungsgruppe | undefined = this.dialogConfig.data.leistungsgruppe;
  form = this.formBuilder.nonNullable.group({
    nummer: [this.leistungsgruppe?.nummer ?? 1, Validators.required],
    bezeichnung: this.formBuilder.nonNullable.group({
      De: [this.leistungsgruppe?.bezeichnung?.De ?? '', Validators.required],
      Fr: [this.leistungsgruppe?.bezeichnung?.Fr ?? ''],
      It: [this.leistungsgruppe?.bezeichnung?.It ?? ''],
    }),
  });
  submitting = false;

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const value = this.form.getRawValue() as ChopLeistungsgruppe;
    const action = this.leistungsgruppe
      ? this.chopService.updateLeistungsgruppe(this.leistungsgruppe.id, { ...this.leistungsgruppe, ...value })
      : this.chopService.createLeistungsgruppe(this.alterskategorieId, value);
    action.pipe(finalize(() => (this.submitting = false))).subscribe({
      next: (result) => this.dialogRef.close(result),
      error: (err) => {
        if (!applyChopServerValidationError(this.form, err?.error, FIELD_MAP)) {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('GLOBAL.NOT_SUCCESSFULLY_SAVED'),
            detail: err?.error?.message,
          });
        }
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
